import PageLayout from "@/Themes/Default/Layouts/PageLayout";
import {withAuthenticationRequired} from "@auth0/auth0-react";

import React, {useMemo, useState} from "react";
import styled from "styled-components";
import {Button} from "primereact/button";
import {useDebounce} from "react-use";
import BaseTextInput from "@/Components/Forms/Form/BaseTextInput";
import UserDatable from "@/Components/Datatables/UserDatable";
import {snakeToNormalCase, url} from "@/Helpers/general";
import {useNavigate} from "react-router-dom";
import BaseDropdown from "@/Components/Forms/Form/BaseDropdown";
import {Role} from "@/Stub/V1";
import useGetUsersFiltersQuery, {UserFilterType} from "@/Hooks/useGetUsersFiltersQuery";
import {useUpdateSearchParams} from "@/Hooks/useUpdateSearchParams";
import {useGetClients} from "@/Service/Api/V1/ApiHooks/Clients/useGetClients";

const StyledWrap = styled.main`
    .content-container {
        margin-top: 2rem;
        padding: 0 1rem;
    }

    .page-actions {
        margin-top: 2rem;
        padding: 1rem;
        background: #fff;
        border-radius: 8px;
    }
`;

const UsersPage: React.FC = () => {
    const navigate = useNavigate();
    const updateUsersFilterHandler = useUpdateSearchParams();
    const {
        pageQuery,
        searchQuery,
        roleQuery,
        pageSizeQuery,
        clientIdQuery
    } = useGetUsersFiltersQuery();

    const [inputValue, setInputValue] = useState(searchQuery || '');


    useDebounce(
        () => {
            updateUsersFilterHandler(UserFilterType.Search, inputValue);
        },
        1000,
        [inputValue]
    );

    const {data} = useGetClients({
        requestParams: {
            page: 1,
            per_page: 1000
        }
    });

    const requestParams = useMemo(() => {
        return {
            page: pageQuery,
            per_page: pageSizeQuery,
            client_id: clientIdQuery === 'all' ? undefined : clientIdQuery,
            role: roleQuery === 'all' ? undefined : roleQuery,
            search: searchQuery || undefined
        };
    }, [searchQuery, roleQuery, pageQuery, pageSizeQuery, clientIdQuery]);

    const organizationOptions = useMemo(() => {
        const clients = data?.data || [];
        return [
            {key: 'all', label: 'All', value: 'all'},
            ...clients.map(client => ({
                key: client.id,
                label: client.name,
                value: client.id.toString()
            }))
        ];
    }, [data]);

    const roleOptions = useMemo(() => {
        return [
            {key: 'all', label: 'All', value: 'all'},
            ...Object.entries(Role).map(([key, value]) => ({
                key,
                value,
                label: snakeToNormalCase(value.replace('ng_', '')),
            }))
        ];
    }, []);

    return (
        <PageLayout>
            <StyledWrap>
                <div className="flex justify-content-between content-header">
                    <div>
                        <h2 className="content-header__title">Users</h2>
                    </div>
                    <Button onClick={() => navigate(url('create-user'))}>Create New User</Button>
                </div>
                <div className="flex align-items-end gap-3">
                    <BaseTextInput
                        value={inputValue || ''}
                        placeholder="Search"
                        onChange={(e) => {
                            setInputValue(e.target.value);
                        }}
                    />
                    <BaseDropdown
                        name="Role"
                        value={roleQuery || 'all'}
                        label="Role"
                        onChange={(e) => {
                            updateUsersFilterHandler(UserFilterType.Role, e.value);
                        }}
                        options={roleOptions}
                    />
                    <BaseDropdown
                        name="Organization"
                        value={clientIdQuery || 'all'}
                        label="Organization"
                        onChange={(e) => {
                            updateUsersFilterHandler(UserFilterType.ClientId, e.value);
                        }}
                        options={organizationOptions}
                    />
                </div>
            </StyledWrap>

            <div className="content-container">
                <UserDatable requestParams={requestParams}/>
            </div>
        </PageLayout>
    );
};

export default withAuthenticationRequired(UsersPage);
